import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { Nullable } from 'constants/types'
import {
    HtmlAttr,
    AriaAttr,
    EventType,
    SlidingDrawerEvent
} from 'constants/enums'
import { ArrayUtils } from 'utils/array'
import { StringUtils } from 'utils/string'
import { AbstractObserver } from 'utils/abstract-classes/abstract-observer'
import { dispatchAnalyticsClickEvent } from 'utils/analytics'
import { DOMUtils } from 'utils/dom'
import { CSSUtils } from 'utils/css'
import { ISailthru } from './types'
import { logger } from 'utils/logger'
import { ScriptService } from 'services/script/script.service'

export enum NewsletterFormType {
    SINGLE = 'single',
    MULTIPLE = 'multiple'
}

const { newsletter: className } = CLASS_NAME
const {
    widget: { newsletter: attrName }
} = ATTR_NAME

export class Newsletter extends AbstractObserver {
    public static scriptSource = 'https://ak.sail-horizon.com/spm/spm.v1.min.js'

    public sailthru: ISailthru
    public listIds: Array<string> = []
    public formType: NewsletterFormType
    private accountId: string
    private emailValue: string

    private form: Nullable<HTMLFormElement>
    private formErrorMsg: Nullable<HTMLElement>
    private formSuccessMsg: Nullable<HTMLElement>
    private successReplaceAreas: Nullable<HTMLElement[]>
    private listCheckboxes: Nullable<HTMLInputElement[]>
    private email: Nullable<HTMLInputElement>
    private emailField: Nullable<HTMLElement>
    private emailError: Nullable<HTMLElement>
    private totalDisplay: Nullable<HTMLElement>

    // eslint-disable-next-line no-magic-numbers
    private readonly drawerTimeout = 5000
    private closeTimeout: ReturnType<typeof setTimeout> | undefined
    private hasSlidingDrawer: boolean
    private submitSuccess: boolean
    private drawerEl: Nullable<HTMLElement>

    public constructor(protected el: HTMLElement) {
        super(el)
        this.activateObserver()
    }

    /**
     * Load the data and show the widget (if data was returned)
     */
    public render(): void {
        // Get Sailthru Bearer authorization from HTML template to authorize Sailthru calls
        const accountId = DOMUtils.getAttr(this.el, attrName.accountId)
        logger('info', 'rendering newsletter')
        if (!accountId) {
            return
        }
        this.accountId = accountId

        if (this.accountId) {
            this.loadSourceScript().catch((err) => logger('error', err))
        } else {
            logger(
                'error',
                'Cannot initialize newsletter component, data-account-id required'
            )
        }

        // Get which form type
        this.formType = DOMUtils.getAttr(
            this.el,
            attrName.type
        ) as NewsletterFormType

        // * Set Elements
        // is the form the component or is the form inside the component:
        this.form = CSSUtils.checkClass(this.el, className.form)
            ? (this.el as HTMLFormElement)
            : this.getElement<HTMLFormElement>(`.${className.form}`, this.el)
        this.formErrorMsg = this.getElement(
            `.${className.formSubmitError}`,
            this.el
        )
        this.formSuccessMsg = this.getElement(
            `.${className.formSubmitSuccess}`,
            this.el
        )
        this.successReplaceAreas = this.getElements(
            `.${className.replaceEl}`,
            this.el
        )
        this.totalDisplay = this.getElement(`[${attrName.total}]`, this.el)

        if (this.form) {
            this.listCheckboxes = this.getElements<HTMLInputElement>(
                `[${attrName.listId}]`,
                this.form
            )
            this.email = this.getElement<HTMLInputElement>(
                `.${className.email}`,
                this.form
            )
            this.emailField = this.getElement(
                `.${className.emailField}`,
                this.form
            )
            this.emailError = this.getElement(
                `.${className.emailError}`,
                this.form
            )
        }

        if (this.formType === NewsletterFormType.SINGLE) {
            // Get Sailthru target list name from HTML template
            const listId = DOMUtils.getAttr(this.el, attrName.listId)
            this.listIds.push(listId)
        } else {
            if (this.form) {
                const drawer = this.getElement(
                    `[${ATTR_NAME.main.slidingDrawer._}]`,
                    this.form
                )
                this.drawerEl = drawer
                this.hasSlidingDrawer = drawer ? true : false
            }

            this.attachChangeListener()
        }
        this.attachSubmitListener()
    }

    // Attach a submit handler
    private attachSubmitListener(): void {
        if (this.form) {
            this.form.addEventListener(EventType.SUBMIT, (event) => {
                event.preventDefault()
                if (this.validateEmail()) {
                    this.submitForm()
                }
            })
        }
    }

    private attachChangeListener(): void {
        if (this.listCheckboxes) {
            this.listCheckboxes.forEach((checkbox) => {
                checkbox.addEventListener(EventType.CHANGE, () => {
                    if (this.closeTimeout) {
                        // cancel banner animation timeout
                        clearTimeout(this.closeTimeout)
                        // unset timeout until the next submit attempt
                        this.closeTimeout = undefined
                        // reset form immediately
                        this.resetForm(false)
                    }

                    const card = checkbox.closest<HTMLElement>(
                        `.${className.card}`
                    )

                    if (checkbox.checked) {
                        this.listIds.push(checkbox.value)
                        CSSUtils.addClass(card, className.cardChecked)
                    } else {
                        this.listIds = ArrayUtils.arrayRemove(
                            this.listIds,
                            checkbox.value
                        )
                        CSSUtils.removeClass(card, className.cardChecked)
                    }

                    this.updateUITotal()

                    if (this.hasSlidingDrawer) {
                        let id
                        if (this.drawerEl) {
                            id = this.drawerEl.id
                        }
                        if (this.listIds.length > 0) {
                            window.dispatchEvent(
                                new CustomEvent(SlidingDrawerEvent.OPEN, {
                                    detail: id
                                })
                            )
                        } else {
                            window.dispatchEvent(
                                new CustomEvent(SlidingDrawerEvent.CLOSE, {
                                    detail: id
                                })
                            )
                        }
                    }
                })
            })
        }
    }

    // Load and initialize Sailthru asynchronously
    private async loadSourceScript(): Promise<void> {
        await DOMUtils.ready

        ScriptService.getInstance()
            .loadScript({
                name: 'newsletter_sailthru',
                src: Newsletter.scriptSource,
                loaded: false
            })
            .then(() => {
                // Initialize Sailthru
                this.sailthru = window.Sailthru
                this.sailthru.init({ customerId: this.accountId })
            })
            .catch((error: Error) => console.error(error))
    }

    // Pull the format form values and call 3rd party API
    // https://getstarted.sailthru.com/developers/api-client/javascript/#userSignUp
    public submitForm(): void {
        if (this.emailValue) {
            // format for sailthru API
            // [listId, listId] => { listId: 1, listId: 1 }
            const signUpLists: Record<string, number> = this.listIds.reduce(
                (obj, item) => ({ ...obj, [item]: 1 }),
                {}
            )

            // Submit data
            this.sailthru.integration('userSignUp', {
                email: this.emailValue,
                lists: signUpLists,
                source: window.location,
                onSuccess: (): void => this.handleSubmitSuccess(),
                onError: (): void => this.handleSubmitError()
            })
            this.sailthru.integration('customEvent', {
                name: 'setOptOutNone',
                email: this.emailValue,
                onSuccess: (): void =>
                    logger(
                        'Success',
                        'Sailthru customEvent setOptOutNone  completed'
                    ),
                onError: (): void =>
                    logger('Error', 'Sailthru customEvent setOptOutNone failed')
            })
        }
    }

    // Sailthru submit success callback
    public handleSubmitSuccess(): void {
        this.submitSuccess = true

        // Hide designated elements on success
        if (this.successReplaceAreas) {
            this.successReplaceAreas.forEach((area) => {
                area.setAttribute(HtmlAttr.HIDDEN, '')
            })
        }
        // remove error class from handleSubmitError
        CSSUtils.removeClass(this.emailField, className.emailFieldError)
        DOMUtils.setAttr(this.formErrorMsg, HtmlAttr.HIDDEN, '')

        // if form type has cards show the signed up success state on the card
        if (this.formType === NewsletterFormType.MULTIPLE && this.form) {
            const checkedCards = this.getElements(
                `.${className.card}.${className.cardChecked}`,
                this.form
            )

            checkedCards.forEach((card) => {
                CSSUtils.addClass(card, className.cardSuccess)

                const checkbox = this.getElement<HTMLInputElement>(
                    'input',
                    card
                )
                DOMUtils.setAttr(checkbox, HtmlAttr.DISABLED, 'true')

                const successMsg = this.getElement(
                    `.${className.cardSuccessMessage}`,
                    card
                )
                if (successMsg) {
                    successMsg.removeAttribute(HtmlAttr.HIDDEN)
                }
            })
        }

        // Show success screen
        if (this.formSuccessMsg) {
            this.formSuccessMsg.removeAttribute(HtmlAttr.HIDDEN)
        }

        // after timeout reset drawer success state and
        // list for another round of sign up
        if (this.formType === NewsletterFormType.MULTIPLE) {
            this.resetForm(true)
        }

        if (this.formType === NewsletterFormType.SINGLE && this.form) {
            dispatchAnalyticsClickEvent(
                this.form.elements[0] as HTMLInputElement,
                'Sign Up (newsletter)',
                'newsletter_widget',
                false
            )
        }
    }

    // Sailthru submit error callback
    public handleSubmitError(): void {
        // Show error screen
        CSSUtils.addClass(this.emailField, className.emailFieldError)
        if (this.formErrorMsg) {
            this.formErrorMsg.removeAttribute(HtmlAttr.HIDDEN)
        }
    }

    // save email value, validate, perform UI feedback
    private validateEmail(): boolean {
        let validEmail = false
        // Collect email value
        if (this.email) {
            this.emailValue = this.email.value
        }

        // Test if email's format is valid
        if (this.emailValue && StringUtils.validateEmail(this.emailValue)) {
            validEmail = true
            // Hide email error
            CSSUtils.removeClass(this.emailField, className.emailFieldError)
            DOMUtils.setAttr(this.emailError, HtmlAttr.HIDDEN, '')
            DOMUtils.setAttr(this.email, AriaAttr.INVALID, 'false')
        } else {
            validEmail = false
            // Show email error
            CSSUtils.addClass(this.emailField, className.emailFieldError)
            if (this.emailError) {
                this.emailError.removeAttribute(HtmlAttr.HIDDEN)
            }
            DOMUtils.setAttr(this.email, AriaAttr.INVALID, 'true')
        }
        return validEmail
    }

    private updateUITotal(): void {
        if (!this.totalDisplay) {
            return
        }
        const total = String(this.listIds.length)
        this.totalDisplay.textContent = total
    }

    private resetForm(animation: boolean): void {
        if (this.hasSlidingDrawer && animation) {
            window.addEventListener(SlidingDrawerEvent.CLOSED, () =>
                this.resetFormUI()
            )
            this.successCloseDrawer()
        } else {
            this.resetFormUI()
        }
    }

    private resetFormUI(): void {
        // reset list IDs & total UI
        if (this.submitSuccess) {
            this.listIds = []
            this.updateUITotal()
            this.submitSuccess = false
        }

        // hide success screen
        DOMUtils.setAttr(this.formSuccessMsg, HtmlAttr.HIDDEN, '')

        // show original screens
        if (this.successReplaceAreas) {
            this.successReplaceAreas.forEach((area) => {
                area.removeAttribute(HtmlAttr.HIDDEN)
            })
        }
    }

    private successCloseDrawer(): void {
        this.closeTimeout = setTimeout(() => {
            window.dispatchEvent(new CustomEvent(SlidingDrawerEvent.CLOSE))
        }, this.drawerTimeout)

        window.removeEventListener(SlidingDrawerEvent.CLOSED, () =>
            this.resetFormUI()
        )
    }
}
